import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import CTABand from "../components/CTABand";
import {
  SERVICES,
  SOLUTIONS,
  TECHNOLOGIES,
  INDUSTRIES,
  WHY_CHOOSE,
  IMAGES,
} from "../data/content";

const Home = () => (
  <main data-testid="home-page">
    {/* Hero */}
    <section className="relative overflow-hidden bg-white" data-testid="home-hero">
      <div className="container-site grid items-center gap-14 py-20 lg:grid-cols-2 lg:py-28">
        <Reveal>
          <div className="mb-5 h-1 w-14 bg-brand-red" aria-hidden="true" />
          <p className="eyebrow">Vaskion Technologies Private Limited</p>
          <h1 className="mt-4 text-4xl font-semibold leading-[1.1] sm:text-5xl lg:text-6xl">
            AI-Powered Software Engineering for Modern Businesses
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-grey md:text-lg">
            We design and build secure, scalable and future-ready digital solutions that transform
            ideas into measurable business value.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact?intent=consultation"
              className="btn-primary"
              data-testid="hero-schedule-consultation-button"
            >
              Schedule a Consultation
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/services"
              className="btn-secondary"
              data-testid="hero-explore-services-button"
            >
              Explore Our Services
            </Link>
          </div>
          <p className="mt-8 font-heading text-xs font-medium uppercase tracking-[0.3em] text-neutral-400">
            Innovate • Build • Transform
          </p>
        </Reveal>
        <Reveal delay={150} className="relative">
          <div
            className="absolute -left-4 -top-4 h-full w-full bg-brand-red/10"
            aria-hidden="true"
          />
          <img
            src={IMAGES.hero}
            alt="Abstract network of connected points representing digital systems"
            className="v-clip relative aspect-[4/3] w-full object-cover"
            loading="eager"
            data-testid="hero-image"
          />
        </Reveal>
      </div>
    </section>

    {/* Services overview */}
    <section className="border-t border-border bg-brand-surface py-20 lg:py-28" data-testid="home-services">
      <div className="container-site">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeader
            eyebrow="What We Do"
            title="Services"
            description="Full-lifecycle software engineering services, from first idea to long-term operation."
          />
          <Link
            to="/services"
            className="inline-flex shrink-0 items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wider text-brand-red transition-colors duration-200 hover:text-brand-redDark"
            data-testid="home-services-view-all"
          >
            View All Services
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {SERVICES.slice(0, 8).map((service, i) => (
            <Reveal key={service.title} delay={(i % 4) * 80}>
              <Link
                to="/services"
                className="group flex h-full flex-col border border-border bg-white p-7 shadow-sm transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-brand-red"
                data-testid={`home-service-card-${i}`}
              >
                <service.icon
                  className="h-7 w-7 text-brand-red"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <h3 className="mt-5 text-lg font-semibold leading-snug">{service.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-grey">
                  {service.description}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Solutions overview */}
    <section className="bg-white py-20 lg:py-28" data-testid="home-solutions">
      <div className="container-site">
        <SectionHeader
          eyebrow="Business Outcomes"
          title="Solutions"
          description="Outcome-oriented solution areas that combine our services to solve real business problems."
        />
        <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
          {SOLUTIONS.map((solution, i) => (
            <Reveal key={solution.title} delay={(i % 3) * 80}>
              <div className="border-t-2 border-brand-graphite pt-6" data-testid={`home-solution-${i}`}>
                <span className="font-heading text-sm font-semibold text-brand-red">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 text-lg font-semibold leading-snug">{solution.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-grey">
                  {solution.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Link
          to="/solutions"
          className="mt-12 inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wider text-brand-red transition-colors duration-200 hover:text-brand-redDark"
          data-testid="home-solutions-view-all"
        >
          Explore Solutions
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </section>

    {/* Technologies */}
    <section className="border-y border-border bg-brand-surface py-20 lg:py-28" data-testid="home-technologies">
      <div className="container-site">
        <SectionHeader
          eyebrow="Our Stack"
          title="Technologies"
          description="A carefully chosen, modern technology stack for building dependable systems."
        />
        <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {TECHNOLOGIES.map((tech, i) => (
            <Reveal key={tech.name} delay={(i % 4) * 60}>
              <div
                className="group border border-border bg-white px-6 py-5 transition-colors duration-200 hover:border-brand-red"
                data-testid={`home-tech-${tech.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="font-heading text-base font-semibold text-brand-graphite transition-colors duration-200 group-hover:text-brand-red">
                  {tech.name}
                </p>
                <p className="mt-1 text-xs text-brand-grey">{tech.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Industries */}
    <section className="bg-white py-20 lg:py-28" data-testid="home-industries">
      <div className="container-site">
        <SectionHeader
          eyebrow="Who We Serve"
          title="Industries"
          description="Domain-aware engineering for sectors where reliability and security matter most."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {INDUSTRIES.map((industry, i) => (
            <Reveal key={industry.title} delay={(i % 4) * 80}>
              <div
                className="flex h-full items-start gap-4 border border-border p-6 transition-colors duration-200 hover:border-brand-graphite"
                data-testid={`home-industry-${i}`}
              >
                <industry.icon
                  className="h-6 w-6 shrink-0 text-brand-red"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <div>
                  <h3 className="font-heading text-base font-semibold">{industry.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-brand-grey">
                    {industry.description}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Why Choose Vaskion */}
    <section className="border-t border-border bg-brand-surface py-20 lg:py-28" data-testid="home-why-choose">
      <div className="container-site grid items-start gap-14 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SectionHeader
            eyebrow="Why Vaskion"
            title="Why Choose Vaskion"
            description="A technology partner focused on outcomes, built on discipline and trust."
          />
          <Reveal delay={150}>
            <img
              src={IMAGES.corporate}
              alt="Corporate building facade seen from below"
              className="v-clip mt-10 aspect-[4/3] w-full object-cover"
              loading="lazy"
              data-testid="why-choose-image"
            />
          </Reveal>
        </div>
        <div className="grid gap-8 sm:grid-cols-2 lg:col-span-3">
          {WHY_CHOOSE.map((item, i) => (
            <Reveal key={item.title} delay={(i % 2) * 100}>
              <div className="flex gap-4" data-testid={`why-choose-item-${i}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-border bg-white">
                  <item.icon className="h-5 w-5 text-brand-red" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-brand-grey">
                    {item.description}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </main>
);

export default Home;
